// Targeted by icon path: unaffected by language or deployment text settings.
const HISTORY_ICON_PREFIXES: readonly string[] = [
  'M16.3997 4C17.2797 4 18 4.7203',
  'M11.7782 12.4425L8.3432 9',
  'M3.33268 3.33366H16.666V13.3337',
];

// One apply() per window, never one per mutation: a streaming response triggers dozens of React
// re-renders per second on the widget.
const APPLY_THROTTLE_MS: number = 200;

export interface PickaxeWidgetPatchRefs {
  panel: HTMLElement;
  closeBtn: HTMLElement;
  deployEl: HTMLElement | null;
}

// The Pickaxe bundle keeps rewriting its own DOM: fixes are idempotent and reapplied
// (MutationObserver + resize) rather than applied once.
export class PickaxeWidgetPatch {
  private readonly _window: (Window & typeof globalThis) | null;
  private readonly _panel: HTMLElement;
  private readonly _closeBtn: HTMLElement;
  private readonly _deployEl: HTMLElement | null;

  private _applyQueued: boolean = false;
  private _applying: boolean = false;
  private _mounted: boolean = false;
  private _observer?: MutationObserver;
  private _resizeHandler?: () => void;
  private _applyTimer?: ReturnType<typeof setTimeout>;
  private _mountTimer?: ReturnType<typeof setTimeout>;
  private _reapplyTimers: ReturnType<typeof setTimeout>[] = [];

  public constructor(refs: PickaxeWidgetPatchRefs) {
    this._window = refs.panel.ownerDocument.defaultView;
    this._panel = refs.panel;
    this._closeBtn = refs.closeBtn;
    this._deployEl = refs.deployEl;
  }

  public onOpen(): void {
    this.watch();
    this.scheduleApply();
    // The widget stays mounted for the component's lifetime: no need to wait again on reopen.
    if (!this._mounted) {
      this.waitForMount(0);
    }
  }

  // Stop observing while the panel is closed: nothing is visible, no need to patch.
  public onClose(): void {
    this._observer?.disconnect();
    this._observer = undefined;
    if (this._resizeHandler && this._window) {
      this._window.removeEventListener('resize', this._resizeHandler);
      this._window.removeEventListener(
        'orientationchange',
        this._resizeHandler
      );
      this._resizeHandler = undefined;
    }
    this.clearTimers();
  }

  public destroy(): void {
    this.onClose();
  }

  private clearTimers(): void {
    clearTimeout(this._applyTimer);
    this._applyTimer = undefined;
    this._applyQueued = false;

    clearTimeout(this._mountTimer);
    this._mountTimer = undefined;

    this._reapplyTimers.forEach((id: ReturnType<typeof setTimeout>) =>
      clearTimeout(id)
    );
    this._reapplyTimers = [];
  }

  private watch(): void {
    if (
      this._observer ||
      !this._deployEl ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }
    this._observer = new MutationObserver(() => this.scheduleApply());
    this._observer.observe(this._deployEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    if (this._window) {
      this._resizeHandler = (): void => this.scheduleApply();
      this._window.addEventListener('resize', this._resizeHandler, {
        passive: true,
      });
      this._window.addEventListener('orientationchange', this._resizeHandler, {
        passive: true,
      });
    }
  }

  private scopeEl(): Element {
    return (
      this._panel.querySelector('.pickaxe-embed') ??
      this._deployEl ??
      this._panel
    );
  }

  private apply(): void {
    if (this._applying) {
      return;
    }
    this._applying = true;
    try {
      const scope: Element = this.scopeEl();
      if (!scope.querySelector) {
        return;
      }
      this.hideByIconPath(scope);
      this.enforceFill();
    } finally {
      this._applying = false;
    }
  }

  private scheduleApply(): void {
    if (this._applyQueued) {
      return;
    }
    this._applyQueued = true;

    this._applyTimer = setTimeout(() => {
      this._applyQueued = false;
      this.apply();
    }, APPLY_THROTTLE_MS);
  }

  private waitForMount(tries: number): void {
    const mounted: Element | null | undefined = this._deployEl?.querySelector(
      '.pickaxe-embed, [class*="pxe-"]'
    );
    if (mounted) {
      this._mounted = true;
      this.apply();
      this._reapplyTimers.push(setTimeout(() => this.apply(), 400));
      this._reapplyTimers.push(setTimeout(() => this.apply(), 1200));
      return;
    }
    if (tries > 200) {
      return;
    }
    this._mountTimer = setTimeout(() => this.waitForMount(tries + 1), 100);
  }

  // Pickaxe sets a fixed inline height without !important: neutralize it here with important priority.
  private forceHeight(el: HTMLElement | null): void {
    if (!el) {
      return;
    }
    const current: string = el.style.getPropertyValue('height');
    const priority: string = el.style.getPropertyPriority('height');
    if (current === '100%' && priority === 'important') {
      return;
    }
    el.style.setProperty('height', '100%', 'important');
    el.style.setProperty('max-height', '100%', 'important');
    el.style.setProperty('min-height', '0', 'important');
    el.style.setProperty('width', '100%', 'important');
  }

  private enforceFill(): void {
    if (!this._deployEl) {
      return;
    }
    this.forceHeight(this._deployEl);
    Array.from(this._deployEl.children).forEach((child: Element) =>
      this.forceHeight(child as HTMLElement)
    );
  }

  private markHidden(el: HTMLElement | null): void {
    if (!el || el === this._closeBtn || el.dataset['pxHidden'] === '1') {
      return;
    }
    el.dataset['pxHidden'] = '1';
    el.classList.add('px-hidden-src');
    el.setAttribute('aria-hidden', 'true');
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
  }

  private hideByIconPath(scope: Element): void {
    scope.querySelectorAll('svg path').forEach((path: Element) => {
      const d: string = path.getAttribute('d') ?? '';
      const matches: boolean = HISTORY_ICON_PREFIXES.some(
        (prefix: string) => d.indexOf(prefix) === 0
      );
      if (!matches) {
        return;
      }
      const btn: HTMLButtonElement | null =
        path.closest<HTMLButtonElement>('button');
      if (btn && btn !== this._closeBtn && !this._closeBtn.contains(btn)) {
        this.markHidden(btn);
      }
    });
  }
}
